import React from 'react';
import PropTypes from 'prop-types';
import InnerHTML from '@Components/InnerHTML';
import classnames from 'classnames';
import UserDatabases from '@Services/UserDatabases';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import get from 'lodash/get';
import CasNumberContainer, { isCasNumberAllowed } from '@Containers/CasNumber';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import { TooltipConstants } from '@Components/Tooltip';
import HoverTooltip from '@Components/HoverTooltip';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';

export default class SubstanceDetailsContentComponent extends React.Component {
  renderDbSpecificContent() {
    const databaseId = get(this.props, 'structure.databaseId');
    let component;

    switch (databaseId) {
      case '1':
        component = (
          <div className="substance-details">
            <ul className="substance-details-left">
              <li>
                <span className="rx-btn-text">
                  <span>Identification</span>
                </span>
              </li>
              <li>
                {get(this.props, 'structure.details.chemicalNameSynonyms.length') && (
                  <span className="rx-btn-text">
                    <span>More names</span>
                  </span>
                )}
              </li>
            </ul>
            <div className="substance-details-right">
              {map(get(this.props, 'structure.pubchemReferences'), (url, title) => (
                <span className="rx-btn-text" key={title}>
                  <InnerHTML tag="span" html={title} />
                </span>
              ))}
            </div>
          </div>
        );
        break;
      case '5':
      case '15':
      case '16':
        component = (
          <div className="substance-details">
            <ul className="substance-details-left">
              <li>
                <span className="rx-btn-text">
                  <span>Identification</span>
                </span>
              </li>
              <li>
                <span className="rx-btn-text">
                  <span>Supplier Information</span>
                </span>
              </li>
            </ul>
            <div className="substance-details-right">
              <strong className="keyword">Largest Quantity:</strong>
              <InnerHTML tag="div" html={get(this.props, 'structure.supplierData.largestQuantity')} />

              <strong className="keyword">Average Price/g ($):</strong>
              <InnerHTML tag="div" html={get(this.props, 'structure.supplierData.avgPriceGram')} />

              <strong className="keyword">Average Price/mmol ($):</strong>
              <InnerHTML tag="div" html={get(this.props, 'structure.supplierData.avgPriceMMol')} />
            </div>
          </div>
        );
        break;
      default: {
        const facts = get(this.props, 'structure.facts') || [];
        const links = get(this.props, 'structure.links') || {};

        component = (
          <div className="substance-details">
            <ul className={classnames('substance-details-left', facts.length > 4 ? 'substance-details-left--columns' : null)}>
              {facts.map((fact, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={index}>
                  <span>
                    {fact.title}&nbsp;{fact.count && <span>- {fact.count}</span>}
                  </span>
                </li>
              ))}
            </ul>

            <div className="substance-details-right">
              {!!links.preparationsCount && (
                <span className="rx-btn-text">
                  <span>Preparations - {links.preparationsCount}</span>
                </span>
              )}

              {!!links.reactionsCount && (
                <span className="rx-btn-text">
                  <span>Reactions - {links.reactionsCount}</span>
                </span>
              )}

              {!!links.targetsCount && (
                <span className="rx-btn-text">
                  <span>Targets - {links.targetsCount}</span>
                </span>
              )}

              {!!links.documentCount && (
                <span className="rx-btn-text">
                  <span>Documents - {links.documentCount}</span>
                </span>
              )}
            </div>
          </div>
        );
        break;
      }
    }

    return component;
  }

  render() {
    const databaseId = get(this.props, 'structure.databaseId');
    const molImage = get(this.props, 'structure.molImage');
    const currentDatabase = UserDatabases.getDatabaseById(databaseId) || {};
    const details = get(this.props, 'structure.details') || {};
    const resultName = get(this.props, 'metadata.resultName');

    return (
      <div>
        {/* Header */}
        <header className="substance__header">
          <InnerHTML
            mode="escapeCustom"
            tag="h2"
            className="substance__title typography-small keyword"
            html={(details.chemicalNames && details.chemicalNames[0]) || `${currentDatabase.name} ID: ${details.id}`}
          />
          <div className="substance__meta">
            {/* formula */}
            {details.formula && (
              <HoverTooltip
                className="substance__meta__item"
                tag="span"
                theme="simple"
                id={TooltipConstants.ids.GlobalHover}
                tooltip="Linear Structure Formula">
                <span className="substance__meta__item__content">
                  <InnerHTML tag="span" html={details.linearFormula} />
                </span>
              </HoverTooltip>
            )}
            {/* weight */}
            {details.weight && (
              <HoverTooltip
                className="substance__meta__item"
                tag="span"
                theme="simple"
                id={TooltipConstants.ids.GlobalHover}
                tooltip="Molecular Weight">
                <span className="substance__meta__item__content">
                  <InnerHTML tag="span" html={details.weight} />
                </span>
              </HoverTooltip>
            )}
            {/* ID */}
            {details.id && (
              <HoverTooltip
                className="substance__meta__item"
                tag="span"
                theme="simple"
                id={TooltipConstants.ids.GlobalHover}
                tooltip={`${currentDatabase.name} ID`}>
                <span className="substance__meta__item__content">
                  <InnerHTML tag="span" html={details.id} />
                </span>
              </HoverTooltip>
            )}
            {/* CAS */}
            {!Auth.isFeatureEnabled(FEATURES.disableCasNumberCta) ? (
              <CasNumberContainer
                className="substance__meta__item"
                casRegistryNumbers={details.casRegistryNumbers}
                substanceId={details.id}
                databaseId={databaseId}
                name={details.chemicalNames && details.chemicalNames[0]}
                structure={molImage && molImage.structure}
                inChiKey={details.inChiKey}
                withCta={false}
                resultName={resultName}
              />
            ) : (
              isCasNumberAllowed(databaseId) &&
              !isEmpty(details.casRegistryNumbers) && (
                <HoverTooltip
                  className="substance__meta__item"
                  tag="span"
                  theme="simple"
                  id={TooltipConstants.ids.GlobalHover}
                  tooltip="CAS Number">
                  <span className="substance__meta__item__content">
                    <InnerHTML
                      tag="span"
                      html={isArray(details.casRegistryNumbers) ? details.casRegistryNumbers.join(', ') : details.casRegistryNumbers}
                    />
                  </span>
                </HoverTooltip>
              )
            )}
          </div>
        </header>
        {/* Db-Specific Content */}
        {this.renderDbSpecificContent()}
      </div>
    );
  }
}

SubstanceDetailsContentComponent.propTypes = {
  // eslint-disable-next-line react/no-unused-prop-types
  structure: PropTypes.object.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  metadata: MetadataPropTypesShape,
};
